import { useState, useEffect, useContext } from "react"
import { useSelector } from 'react-redux'
import { ModalKey, ModalContext } from "components/Modals/ModalContext"
import { WhichWeightAPI } from "WhichWeightAPI"
import { CapitalizeFirstLetter, GetProgressionLevelName } from "functions/textUtils"
import moment from "moment"

import ContentFrame from 'components/Frame/ContentFrame'
import BrandColorButton from "components/Buttons/BrandColorButton"

export default function FitnessProfileTab() {

    const { ShowModal } = useContext(ModalContext)

    const profileType = useSelector(state => state.athleteProfile.profileType)
    const groupId = useSelector(state => state.athleteProfile.groupId)
    const athleteId = useSelector(state => state.athleteProfile.userId)

    const [fitnessProfile, setFitnessProfile] = useState(null)

    const GetFitnessProfileFromBackend = async () => {

        if (profileType === 'group') {
            const jsonBody = {
                groupId: groupId,
            }

            // console.log(jsonBody);

            const result = await WhichWeightAPI('REPORTING', 'GET', '/group_fitness_profile', jsonBody)

            // console.log(result.data.data);

            if (result.data.status === 'failure') {
                setFitnessProfile(null)
                console.error(result)
                return
            }

            setFitnessProfile(result.data.data)
        }

        if (profileType === 'individual') {
            const jsonBody = {
                traineeId: athleteId,
            }

            // console.log(jsonBody);

            const result = await WhichWeightAPI('CORE', 'GET', '/trainee_fitness_profile', jsonBody)

            // console.log(result.data.data);

            if (result.data.status === 'failure') {
                setFitnessProfile(null)
                console.error(result)
                return
            }

            setFitnessProfile(result.data.data)
        }

    }

    const OnEditFitnessProfile = () => {

        const modalData = {
            key: ModalKey.EditFitnessProfile,
            size: 'half',
            profileType: profileType,
            groupId: groupId,
            userId: athleteId,
            fitnessProfile: fitnessProfile,
            boilerplateEnabled: 0
        }

        ShowModal(modalData)

    }

    useEffect(() => { GetFitnessProfileFromBackend() }, [])

    if (fitnessProfile === null) {
        return (
            <div className='w-full h-80 bg-gray-200 rounded-lg animate-pulse' />
        )
    }

    if (fitnessProfile.id === null) {
        return (
            <>
                <div className="flex items-center">
                    <div className="flex-grow text-lg">Fitness Profile</div>
                    
                    <div className="w-32">
                        <BrandColorButton
                            size='extra-small'
                            text="Edit"
                            action={() => OnEditFitnessProfile()}
                        />
                    </div>
                </div>

                <ContentFrame>
                    <div className="text-center text-gray-500 py-4">No fitness profile found</div>
                </ContentFrame>
            </>
        )
    }

    return (
        <>
            <div className="flex items-center">
                <div className="flex-grow text-lg">Fitness Profile</div>
                
                <div className="w-32">
                    <BrandColorButton
                        size='extra-small'
                        text="Edit"
                        action={() => OnEditFitnessProfile()}
                        className="shadow-md"
                    />
                </div>
            </div>

            {profileType === 'group' && (
                <ContentFrame>
                    <div className="w-full px-8 py-6 space-y-4">
                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Average Group Age</div>
                            <div>{fitnessProfile.defaultAverageAge} years old</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Default Group Biological Gender</div>
                            <div>{CapitalizeFirstLetter(fitnessProfile.defaultGender)}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Measurement System</div>
                            <div>{CapitalizeFirstLetter(fitnessProfile.measurementSystem)}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Average Group Height</div>
                            <div>{Math.floor(fitnessProfile.defaultAverageHeight / 12)}' {fitnessProfile.defaultAverageHeight % 12}"</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Average Group Weight</div>
                            <div>{fitnessProfile.defaultAverageWeight} lbs</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Sport</div>
                            <div>{fitnessProfile.sportName}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Average Training Days Per Week</div>
                            <div>{fitnessProfile.daysOfTraining} day{fitnessProfile.daysOfTraining !== 1 ? 's' : ''}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Months Trained Over the Past Year</div>
                            <div>{fitnessProfile.monthsOfTraining} month{fitnessProfile.monthsOfTraining !== 1 ? 's' : ''}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Years of Strength Training</div>
                            <div>{fitnessProfile.yearsOfTraining} year{fitnessProfile.yearsOfTraining !== 1 ? 's' : ''}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Progression Level</div>
                            <div>{GetProgressionLevelName(fitnessProfile.progressionLevel)}</div>
                        </div>
                    </div>
                </ContentFrame>
            )}

            {profileType === 'individual' && (
                <ContentFrame>
                    <div className="w-full px-8 py-6 space-y-4">
                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Date of Birth</div>
                            <div>{moment(fitnessProfile.birthday).format('MMMM D, YYYY')}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Biological Gender</div>
                            <div>{CapitalizeFirstLetter(fitnessProfile.gender)}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Measurement System</div>
                            <div>{CapitalizeFirstLetter(fitnessProfile.measurementSystem)}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Height</div>
                            <div>{Math.floor(fitnessProfile.height / 12)}' {fitnessProfile.height % 12}"</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Weight</div>
                            <div>{fitnessProfile.weight} lbs</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Sport</div>
                            <div>{fitnessProfile.sportName}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Average Training Days Per Week</div>
                            <div>{fitnessProfile.daysOfTraining} day{fitnessProfile.daysOfTraining !== 1 ? 's' : ''}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Months Trained Over the Past Year</div>
                            <div>{fitnessProfile.monthsOfTraining} month{fitnessProfile.monthsOfTraining !== 1 ? 's' : ''}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Years of Strength Training</div>
                            <div>{fitnessProfile.yearsOfTraining} year{fitnessProfile.yearsOfTraining !== 1 ? 's' : ''}</div>
                        </div>

                        <div className="flex text-gray-500">
                            <div className="flex-grow font-semibold">Progression Level</div>
                            <div>{GetProgressionLevelName(fitnessProfile.progressionLevel)}</div>
                        </div>
                    </div>
                </ContentFrame>
            )}
        </>
    )
}